import React, { useState, useEffect } from 'react';
import { api } from '../config/api';
import { FiMail, FiCheckCircle, FiXCircle, FiClock } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { tw, monoFont } from '../utils/terminalTheme';

interface EmailDocument {
  id: number;
  filename: string;
  from_email: string;
  email_subject: string;
  created_at: string;
  processing_status: string;
  property_id: number | null;
}

export const EmailStatus: React.FC = () => {
  const [emailDocs, setEmailDocs] = useState<EmailDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [webhookEndpoint, setWebhookEndpoint] = useState('');

  useEffect(() => {
    loadEmailDocuments();
    const baseUrl = 'https://real-expense-doc-tracker-production.up.railway.app';
    setWebhookEndpoint(`${baseUrl}/api/v1/webhook/sendgridion`);
  }, []);

  const loadEmailDocuments = async () => {
    try {
      setLoading(true);
      const response = await api.get('/documents/', {
        params: { limit: 50 }
      });

      const emailDocs = response.data.filter((doc: any) => doc.from_email);
      setEmailDocs(emailDocs.sort((a: EmailDocument, b: EmailDocument) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ));
    } catch (error) {
      console.error('Failed to load email documents:', error);
      toast.error('Failed to load email documents');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'filed':
        return <FiCheckCircle className="text-green-400 w-3.5 h-3.5" />;
      case 'pending':
      case 'processing':
        return <FiClock className="text-amber-400 w-3.5 h-3.5" />;
      default:
        return <FiXCircle className="text-red-400 w-3.5 h-3.5" />;
    }
  };

  const getStatusBadge = (status: string) => {
    let colors = 'bg-gray-500/15 text-gray-400 border-gray-500/30';
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'filed':
        colors = 'bg-green-500/15 text-green-400 border-green-500/30';
        break;
      case 'pending':
      case 'processing':
        colors = 'bg-amber-500/15 text-amber-400 border-amber-500/30';
        break;
      case 'failed':
      case 'error':
        colors = 'bg-red-500/15 text-red-400 border-red-500/30';
        break;
    }
    return (
      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10px] font-mono border ${colors}`}>
        {getStatusIcon(status)}
        {status}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className={tw.page}>
      {/* Terminal Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold font-mono text-white" style={monoFont}>
          <span className="text-cyan-400">~/</span>settings/<span className="text-cyan-400">email-status</span>
          <span className="text-gray-600 text-sm font-normal ml-3">
            <span className="text-green-500">$</span> monitor --inbox
          </span>
        </h1>
        <p className="text-gray-500 text-sm font-mono mt-1">
          Monitor the status of email-to-expense processing
        </p>
      </div>

      {/* Webhook Configuration */}
      <div className="bg-gray-800/80 border border-gray-700/50 rounded-lg p-5 mb-6">
        <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider mb-4">Webhook Configuration</h2>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-xs font-mono">Webhook Endpoint:</span>
            <code className="text-cyan-400 bg-gray-900 px-2 py-1 rounded text-xs font-mono border border-gray-700/50">{webhookEndpoint}</code>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-gray-500 text-xs font-mono">Email Address:</span>
            <code className="text-cyan-400 bg-gray-900 px-2 py-1 rounded text-xs font-mono border border-gray-700/50">receipts@gpume.com</code>
          </div>
        </div>
        <div className="mt-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg">
          <p className="text-amber-400/80 text-xs font-mono">
            <span className="text-amber-400 font-medium">Troubleshooting:</span> Emails must be sent from whitelisted addresses.
            Check the Email Whitelist page to ensure your sender address is authorized.
          </p>
        </div>
      </div>

      {/* Email Documents Table */}
      <div className={tw.tableWrap}>
        <div className="px-5 py-3 border-b border-gray-700/50 flex justify-between items-center">
          <h2 className="text-xs font-mono text-gray-500 uppercase tracking-wider">
            Recent Email Documents
          </h2>
          <button
            onClick={loadEmailDocuments}
            className={tw.btnPrimary}
          >
            Refresh
          </button>
        </div>

        {loading ? (
          <div className={tw.empty}>
            <div className={tw.loading + ' mx-auto mb-3'}></div>
            <span className="text-gray-500 text-xs font-mono">Loading email documents...</span>
          </div>
        ) : emailDocs.length === 0 ? (
          <div className={tw.empty}>
            <FiMail className="w-10 h-10 text-gray-700 mx-auto mb-3" />
            <span className="text-gray-500 text-xs font-mono">No email documents found</span>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-gray-700/30">
                  <th className={tw.tableHeader}>Status</th>
                  <th className={tw.tableHeader}>From</th>
                  <th className={tw.tableHeader}>Subject</th>
                  <th className={tw.tableHeader}>Filename</th>
                  <th className={tw.tableHeader}>Property</th>
                  <th className={tw.tableHeader}>Received</th>
                </tr>
              </thead>
              <tbody className={tw.tableDivider}>
                {emailDocs.map((doc) => (
                  <tr key={doc.id} className={tw.tableRow}>
                    <td className={tw.tableCell}>
                      {getStatusBadge(doc.processing_status)}
                    </td>
                    <td className={tw.tableCell}>
                      <span className="text-sm text-gray-400 font-mono">{doc.from_email}</span>
                    </td>
                    <td className={tw.tableCell}>
                      <span className="text-sm text-white font-mono">{doc.email_subject || 'No subject'}</span>
                    </td>
                    <td className={tw.tableCell}>
                      <span className="text-[10px] text-gray-500 font-mono">{doc.filename}</span>
                    </td>
                    <td className={tw.tableCell}>
                      <span className="text-sm text-gray-400 font-mono">
                        {doc.property_id ? `Property ${doc.property_id}` : 'Pending Review'}
                      </span>
                    </td>
                    <td className={tw.tableCell}>
                      <span className="text-xs text-gray-500 font-mono">{formatDate(doc.created_at)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
